<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, ShieldAlert } from 'lucide-svelte';
  import { debtApi } from '$lib/api/debt';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { sevKind } from '$lib/utils/format';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import DebtModal from './DebtModal.svelte';

  const { projectId }: { projectId: number } = $props();
  const queryClient = useQueryClient();

  const debtQuery = createQuery(() => ({
    queryKey: ['project-debt', projectId],
    queryFn: () => debtApi.forProject(projectId)
  }));

  // Display order: open entries first, then resolved — stable within each
  // group (matches the prototype's `[...open, ...resolved]`).
  const ordered = $derived.by(() => {
    const all = debtQuery.data?.results ?? [];
    return [...all.filter((e) => !e.resolved), ...all.filter((e) => e.resolved)];
  });
  const openCount = $derived((debtQuery.data?.results ?? []).filter((e) => !e.resolved).length);
  const resolvedCount = $derived((debtQuery.data?.results ?? []).length - openCount);

  const resolveMutation = createMutation(() => ({
    mutationFn: ({ id, resolved }: { id: number; resolved: boolean }) => debtApi.update(id, { resolved }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['project-debt', projectId] });
      toast.success(vars.resolved ? 'Marked resolved' : 'Reopened');
    }
  }));

  let showModal = $state(false);
</script>

<div style="display:flex;align-items:center;margin-bottom:16px">
  <div style="font-size:13px;color:var(--t3)">{openCount} open · {resolvedCount} resolved</div>
  <span style="flex:1"></span>
  <button type="button" class="btn btn-secondary" onclick={() => (showModal = true)}><Plus size={16} />Add entry</button>
</div>

{#if debtQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading debt…</p>
{:else if debtQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(debtQuery.error)}</p>
{:else if ordered.length}
  <div class="card">
    {#each ordered as e, i (e.id)}
      {#if i > 0}<div class="divider"></div>{/if}
      <div class="debt-item {e.resolved ? 'done' : ''}">
        <div style="flex:1">
          <div class="debt-desc {e.resolved ? 'done' : ''}">{e.desc}</div>
          {#if e.ceiling}<div class="debt-ceiling">{e.ceiling}</div>{/if}
        </div>
        <div style="display:flex;align-items:center;gap:12px;flex-shrink:0">
          {#if e.resolved}
            <Chip label="Resolved" kind="lime" dot />
          {:else}
            <Chip label={e.severity[0].toUpperCase() + e.severity.slice(1)} kind={sevKind(e.severity)} />
          {/if}
          <button type="button" class="btn btn-ghost btn-sm" onclick={() => resolveMutation.mutate({ id: e.id, resolved: !e.resolved })}>
            {e.resolved ? 'Reopen' : 'Resolve'}
          </button>
        </div>
      </div>
    {/each}
  </div>
{:else}
  <EmptyState
    icon={ShieldAlert}
    heading="No debt logged"
    sub={'Track shortcuts and their acceptable ceilings here — e.g. "O(n²) scan, fine under 1k rooms".'}
  >
    {#snippet action()}
      <button type="button" class="btn btn-secondary" onclick={() => (showModal = true)}>Add entry</button>
    {/snippet}
  </EmptyState>
{/if}

{#if showModal}
  <DebtModal {projectId} onClose={() => (showModal = false)} />
{/if}
